/**
 * Pure helpers behind bulk task selection in the Space List view.
 *
 * Deleting a task deletes its whole subtree on the server
 * (`tasks.cascade.js` walks Task → Subtask → Sub-subtask), so the UI cannot
 * pretend a parent and its subtasks are independent picks: ticking a parent
 * takes its children with it whether or not their boxes are ticked. These
 * helpers make that one model — the checkbox states, the "N tasks, M subtasks"
 * confirmation and the request body all derive from it, instead of three
 * approximations that can disagree.
 *
 * `childrenByParent` is a `Map<parentId, task[]>` built from the loaded task
 * array. Build it from the UNFILTERED list: a hidden subtask is still deleted,
 * so counting off the filtered index would understate the damage.
 */

/**
 * Every descendant of `rootIds`, at any depth, excluding the roots themselves.
 *
 * Breadth of the walk mirrors the server cascade rather than assuming one
 * level — the tree is three deep (`MAX_SUBTASK_DEPTH = 2`). The `seen` guard
 * doubles as a cycle break, so a corrupted `parentTaskId` loop terminates
 * instead of hanging the tab.
 */
export function collectDescendants(rootIds, childrenByParent) {
  const seen = new Set()
  const frontier = [...rootIds].map(String)
  while (frontier.length > 0) {
    const id = frontier.pop()
    for (const child of childrenByParent.get(id) ?? []) {
      const cid = String(child.id)
      if (seen.has(cid)) continue
      seen.add(cid)
      frontier.push(cid)
    }
  }
  return seen
}

/**
 * Drop every id that another selected id already covers.
 *
 * Two jobs in one function. It keeps the selection CANONICAL — ticking a
 * parent replaces its children rather than sitting alongside them, so
 * unticking that parent clears the whole subtree instead of leaving orphaned
 * ticks that pop back into view. And because the survivors are exactly the
 * roots, the resulting Set is also the request body: sending a child next to
 * its selected parent is redundant (the server cascades anyway) and only eats
 * into the 500-id cap.
 *
 * @returns {Set<string>} the pruned selection / the ids to send
 */
export function prunedSelection(selectedIds, childrenByParent) {
  const covered = collectDescendants(selectedIds, childrenByParent)
  const out = new Set()
  for (const id of selectedIds) {
    const s = String(id)
    if (!covered.has(s)) out.add(s)
  }
  return out
}

/**
 * What the user is actually about to lose, split the way the UI names things:
 * a top-level row is a "task", anything nested under one is a "subtask"
 * (depth-2 sub-subtasks included — they are still lost, and a third number
 * would be noise in a confirmation).
 *
 * Counts the implied descendants too, which is the whole point: a selection of
 * one parent reads as "1 task and 4 subtasks", not "1 task".
 *
 * @param {(id: string) => boolean} isTopLevel
 */
export function selectionCounts(selectedIds, childrenByParent, isTopLevel) {
  const roots = [...selectedIds].map(String)
  const descendants = collectDescendants(roots, childrenByParent)
  let tasks = 0
  let subtasks = 0
  for (const id of roots) {
    if (isTopLevel(id)) tasks += 1
    else subtasks += 1
  }
  return {
    tasks,
    subtasks: subtasks + descendants.size,
    total: roots.length + descendants.size,
  }
}

/**
 * Tri-state for a group's header checkbox: 'none' | 'some' | 'all'.
 * 'some' drives the native `indeterminate` flag.
 */
export function groupCheckState(groupIds, selectedIds) {
  if (groupIds.length === 0) return 'none'
  let hit = 0
  for (const id of groupIds) if (selectedIds.has(String(id))) hit += 1
  if (hit === 0) return 'none'
  return hit === groupIds.length ? 'all' : 'some'
}
